import { disposeMesh } from 'src/utils/object3DHelpers';
import { SceneComponent, ComponentInteractionType } from '../SceneComponent';

interface Inputs {
  size: { x: number; y: number; z: number };
  period: number;
  width: number;
  height: number;
  url: string | null;
}

export class LoaderIndicator extends SceneComponent {
  private root: THREE.Object3D;

  private pivot: THREE.Object3D;

  private plane: THREE.Mesh | null;

  private box?: THREE.Mesh;

  private edges?: THREE.LineSegments;

  private mixer: THREE.AnimationMixer;

  inputs: Inputs = {
    size: { x: 1, y: 1, z: 1 },
    period: 8,
    width: 0.998 * 0.5,
    height: 0.36 * 0.5,
    url: null // Treedis logo
  };

  // setting which events are compatible with the component
  events = {
    [ComponentInteractionType.CLICK]: false,
    [ComponentInteractionType.HOVER]: false,
    [ComponentInteractionType.DRAG]: false
  };

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    this.pivot = new THREE.Object3D();
    // setting the collider to add the click event
    // this.outputs.collider = this.root;
    this.outputs.objectRoot = this.root;
    this.root.add(this.pivot);
    this.root.position.set(0, this.inputs.size.y * 0.25, 0);
    this.root.scale.set(0.5, 0.5, 0.5);

    this.makePlane();
    // this.makeBox();
    this.mixer = new THREE.AnimationMixer(this.root);
    this.playAnimation();
  }

  onDestroy() {
    if (this.plane) {
      disposeMesh(this.plane);
      this.plane = null;
    }
    if (this.box) {
      disposeMesh(this.box);
      this.box = undefined;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = undefined;
    }
  }

  onInputsUpdated() {
    this.playAnimation();
  }

  private makePlane() {
    if (this.plane) {
      disposeMesh(this.plane);
      this.plane = null;
    }

    const { width, height, url } = this.inputs;

    const THREE = this.context.three;
    const geometry = new THREE.PlaneGeometry(width, height, 10, 10);

    const _this = this;
    new THREE.TextureLoader().load(
      url,
      // onLoad callback
      function (texture) {
        try {
          if (texture != null && texture.isTexture) {
            // eslint-disable-next-line no-multi-assign
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.encoding = THREE.sRGBEncoding;
            texture.repeat.set(1, 1);
            if (
              texture.image.width > _this.context.renderer.capabilities.maxTextureSize ||
              texture.image.height > _this.context.renderer.capabilities.maxTextureSize
            ) {
              texture.image.height =
                texture.image.height *
                (texture.image.height / _this.context.renderer.capabilities.maxTextureSize);
              texture.image.width =
                texture.image.width *
                (texture.image.width / _this.context.renderer.capabilities.maxTextureSize);
            }
            if (texture.image.width == 0 || texture.image.height == 0) {
              throw new Error('Unable to get image resolution.');
            }
            const material = new THREE.MeshBasicMaterial({
              map: texture,
              blending: THREE.NormalBlending,
              transparent: true,
              depthWrite: false
            });
            _this.plane = new THREE.Object3D();
            const faceFront = new THREE.Mesh(geometry, material);
            const faceBack = new THREE.Mesh(geometry, material);
            faceBack.rotation.set(0, Math.PI, 0);

            if (_this.plane) {
              _this.plane.add(faceFront);
              _this.plane.add(faceBack);
              _this.pivot.add(_this.plane);
            }
          }
        } catch (e) {
          console.log('[Err] An error happened during texture init.', e);
        }
      },
      // onProgress callback currently not supported
      undefined,
      // onError callback
      function (err) {
        console.error('[Err] An error happened during texture load.', err);
      }
    );
  }

  // @ts-ignore wireframe box, currently not used
  private makeBox() {
    const THREE = this.context.three;

    if (this.box) {
      disposeMesh(this.box);
      this.box = undefined;
    }
    if (this.edges) {
      disposeMesh(this.edges);
      this.edges = undefined;
    }

    const boxGeometry: THREE.BoxGeometry = new THREE.BoxGeometry(
      this.inputs.size.x,
      this.inputs.size.y,
      this.inputs.size.z
    );

    const boxMaterial: THREE.MeshBasicMaterial = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0,
      depthWrite: false
    });
    boxMaterial.transparent = true;
    boxMaterial.side = THREE.BackSide;
    boxMaterial.blending = THREE.AdditiveBlending;
    this.box = new THREE.Mesh(boxGeometry, boxMaterial);

    const edgesGeometry = new THREE.EdgesGeometry(boxGeometry);
    this.edges = new THREE.LineSegments(
      edgesGeometry,
      new THREE.LineBasicMaterial({
        transparent: true,
        color: 0xffffff,
        linewidth: 2,
        opacity: 1
      })
    );
    if (this.edges) {
      this.root?.add(this.edges);
    }
  }

  private playAnimation() {
    const THREE = this.context.three;

    const yAxis = new THREE.Vector3(0, 1, 0);
    const frame0 = new THREE.Quaternion().setFromAxisAngle(yAxis, 0);
    const frame1 = new THREE.Quaternion().setFromAxisAngle(yAxis, Math.PI);
    const frame2 = new THREE.Quaternion().setFromAxisAngle(yAxis, Math.PI * 2);
    const track = new THREE.QuaternionKeyframeTrack(
      '.quaternion',
      [0, this.inputs.period * 0.5, this.inputs.period],
      [
        frame0.x,
        frame0.y,
        frame0.z,
        frame0.w,
        frame1.x,
        frame1.y,
        frame1.z,
        frame1.w,
        frame2.x,
        frame2.y,
        frame2.z,
        frame2.w
      ]
    );

    const clip = new THREE.AnimationClip(null, this.inputs.period, [track]);
    const action: THREE.AnimationAction = this.mixer.clipAction(clip, this.root);
    action.play();

    const onEnterTime = 0.2;
    const scaleTrack = new THREE.VectorKeyframeTrack(
      '.scale',
      [0, onEnterTime],
      [0, 0, 0, this.inputs.size.x, this.inputs.size.y, this.inputs.size.z],
      THREE.InterpolateSmooth
    );
    const onEnterClipGroup = new THREE.AnimationClip(null, onEnterTime, [scaleTrack]);
    const onEnterActionGroup: THREE.AnimationAction = this.mixer.clipAction(
      onEnterClipGroup,
      this.root
    );
    onEnterActionGroup.loop = THREE.LoopOnce;
    onEnterActionGroup.clampWhenFinished = true;
    onEnterActionGroup.play();
  }

  onTick(delta: number) {
    if (this.mixer) {
      this.mixer.update(delta / 1000);
    }
  }
}

export const loaderIndicatorType = 'treedis.loaderIndicator';
export const makeLoaderIndicator = (): any => new LoaderIndicator();
